import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import { CheckCircle, Receipt, Home } from '@mui/icons-material';
import paymentService from '../services/paymentService';
import { PaymentStatusResponse } from '../types';

const PaymentSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const factureId = searchParams.get('factureId');

  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatusResponse | null>(null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!factureId) {
        setError('ID de facture manquant');
        setLoading(false);
        return;
      }

      try {
        const status = await paymentService.getPaymentStatus(parseInt(factureId));
        setPaymentStatus(status);
      } catch (err: any) {
        console.error('Erreur lors de la vérification du statut:', err);
        setError('Impossible de vérifier le statut du paiement');
      } finally {
        setLoading(false);
      }
    };

    checkPaymentStatus();
  }, [factureId]);

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h6">Vérification du paiement...</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Veuillez patienter pendant que nous confirmons votre paiement
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (error || !paymentStatus) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error || 'Une erreur est survenue'}
          </Alert>
          <Button
            variant="contained"
            startIcon={<Home />}
            onClick={() => navigate('/')}
          >
            Retour à l'accueil
          </Button>
        </Paper>
      </Container>
    );
  }

  const isPaymentSuccessful =
    paymentStatus.factureStatut === 'PAYEE' ||
    paymentStatus.stripePaymentStatus === 'SUCCEEDED';

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper
        sx={{
          p: 4,
          textAlign: 'center',
          background: isPaymentSuccessful
            ? 'linear-gradient(135deg, #E8F5E9 0%, #F1F8E9 100%)'
            : 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)',
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: isPaymentSuccessful ? '#4CAF50' : '#FF9800',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
          }}
        >
          <CheckCircle sx={{ fontSize: 48, color: 'white' }} />
        </Box>

        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: '#1B5E20' }}>
          {isPaymentSuccessful ? 'Paiement réussi !' : 'Paiement en cours de traitement'}
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          Facture : <strong>{paymentStatus.factureNumero}</strong>
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          {paymentStatus.message}
        </Typography>

        {isPaymentSuccessful && paymentStatus.stripePaymentDate && (
          <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="body2">
              <strong>Date de paiement :</strong>{' '}
              {new Date(paymentStatus.stripePaymentDate).toLocaleString('fr-FR')}
            </Typography>
            {paymentStatus.stripePaymentIntentId && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>ID de transaction :</strong> {paymentStatus.stripePaymentIntentId}
              </Typography>
            )}
          </Alert>
        )}

        {!isPaymentSuccessful && (
          <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="body2">
              Votre paiement est en cours de traitement. Vous recevrez une confirmation par email dès que le paiement sera validé.
            </Typography>
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<Receipt />}
            onClick={() => navigate(`/factures/${factureId}`)}
            sx={{
              bgcolor: '#2E7D32',
              '&:hover': {
                bgcolor: '#1B5E20',
              },
            }}
          >
            Voir la facture
          </Button>
          <Button
            variant="outlined"
            startIcon={<Home />}
            onClick={() => navigate('/')}
          >
            Retour à l'accueil
          </Button>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 4, display: 'block' }}>
          Un email de confirmation vous a été envoyé
        </Typography>
      </Paper>
    </Container>
  );
};

export default PaymentSuccessPage;
